// AI Assistant Default Configuration
// This file contains ONLY default values - no environment variable checks
// Environment variables are checked in colors.ts and config.ts where they're used

type ExamplePrompt = {
    tier: "Basic" | "Procedural" | "Advanced" | "Analytical";
    question: string;
    why: string;
};

export const AI_ASSISTANT_CONFIG = {
    // API Configuration
    apiUrl: "http://localhost:8000",

    // UI Configuration
    showBranding: false, // Hide logo/branding and controls for acetrials-frontend integration (set REACT_APP_SHOW_BRANDING=true for standalone)
    defaultTheme: "light" as "light" | "dark", // Light theme for acetrials-frontend (set REACT_APP_DEFAULT_THEME=dark for standalone)
    isStandalone: false, // False for acetrials-frontend (embedded), true for standalone full-page app (set REACT_APP_IS_STANDALONE=true)

    // Branding
    appName: "Acetrials AI Assistant",
    appDescription:
        "Acetrials AI platform for clinical trials and protocol analysis",
    brandName: "Acetrials AI Assistant",
    appLogo: "/logo_pmt.png",
    modelName: "AI Model",

    // UI Text
    welcomeTitle: "How can I help you today?",
    welcomeDescription:
        "Upload clinical trial FAQs and regulatory documents to unlock intelligent, context-aware answers.",
    inputPlaceholder: "Ask anything about PMT clinical trials...",
    footerText: "Private & Secure",

    // Light Theme Colors (Acetrials Orange #f58024)
    colors: {
        light: {
            background: "ffffff",
            foreground: "18181b",
            card: "f9fafb",
            cardForeground: "18181b",
            popover: "ffffff",
            popoverForeground: "18181b",
            primary: "f58024",
            primaryForeground: "ffffff",
            primaryHover: "d66a1a",
            secondary: "f4f4f5",
            secondaryForeground: "18181b",
            muted: "f4f4f5",
            mutedForeground: "71717a",
            accent: "f4f4f5",
            accentForeground: "18181b",
            destructive: "dc2626",
            destructiveForeground: "ffffff",
            border: "e4e4e7",
            input: "e4e4e7",
            ring: "f58024",
            brandReasoning: "f58024",
            brandReasoningLight: "f58024",
            statusWarning: "f59e0b",
            statusSuccess: "16a34a",
            chipBasic: "6366f1",
            chipProcedural: "8b5cf6",
            chipAdvanced: "f59e0b",
            chipAnalytical: "ec4899",
        },
        dark: {
            background: "09090b",
            foreground: "fafafa",
            card: "111113",
            cardForeground: "fafafa",
            popover: "09090b",
            popoverForeground: "fafafa",
            primary: "f58024",
            primaryForeground: "ffffff",
            primaryHover: "d66a1a",
            secondary: "1e1e21",
            secondaryForeground: "fafafa",
            muted: "1e1e21",
            mutedForeground: "a1a1aa",
            accent: "1e1e21",
            accentForeground: "fafafa",
            destructive: "7f1d1d",
            destructiveForeground: "fafafa",
            border: "27272a",
            input: "27272a",
            ring: "f58024",
            brandReasoning: "f58024",
            brandReasoningLight: "f58024",
            statusWarning: "f59e0b",
            statusSuccess: "16a34a",
            chipBasic: "6366f1",
            chipProcedural: "8b5cf6",
            chipAdvanced: "f59e0b",
            chipAnalytical: "ec4899",
        },
    },

    // Example Prompts
    examplePrompts: [
        {
            tier: "Basic" as const,
            question:
                "What is the difference between a protocol deviation and violation?",
            why: "Basic terminology — tests single-document retrieval",
        },
        {
            tier: "Basic" as const,
            question: "What are the essential elements of informed consent?",
            why: "Comprehensive lists — demonstrates structured extraction",
        },
        {
            tier: "Procedural" as const,
            question:
                "What training is required before obtaining informed consent?",
            why: "Multi-document — cross-references training & consent requirements",
        },
        {
            tier: "Procedural" as const,
            question: "How should I prepare for an FDA audit or inspection?",
            why: "Practical guidance — synthesizes audit & documentation requirements",
        },
        {
            tier: "Advanced" as const,
            question:
                "What are my responsibilities as a principal investigator per GCP?",
            why: "Role-based synthesis — aggregates PI duties from multiple sources",
        },
        {
            tier: "Advanced" as const,
            question: "Walk me through reporting a serious adverse event",
            why: "Step-by-step procedures — demonstrates sequential guidance",
        },
        {
            tier: "Analytical" as const,
            question: "Compare monitoring visits and FDA audit preparation",
            why: "Analytical comparison — shows cross-topic reasoning",
        },
        {
            tier: "Analytical" as const,
            question: "A participant wants to withdraw consent—what do I do?",
            why: "Scenario troubleshooting — tests real-world application",
        },
    ],
};
